import type {
  CoverageData,
  FormConfig,
  StoreSettings,
  StoreSettingsPatch,
  TrackingPixel,
} from '@cod/config-schema';
import http from './axios.js';

export interface AnalyticsSummary {
  form_viewed: number;
  form_opened: number;
  form_submitted: number;
  order_created: number;
}

export async function getConfig(): Promise<FormConfig> {
  const { data } = await http.get<FormConfig>('/api/config');
  return data;
}

export async function putConfig(config: FormConfig): Promise<FormConfig> {
  const { data } = await http.put<FormConfig>('/api/config', config);
  return data;
}

export async function getSettings(): Promise<StoreSettings> {
  const { data } = await http.get<StoreSettings>('/api/settings');
  return data;
}

export async function putSettings(patch: StoreSettingsPatch): Promise<StoreSettings> {
  const { data } = await http.put<StoreSettings>('/api/settings', patch);
  return data;
}

export async function getAnalyticsSummary(): Promise<AnalyticsSummary> {
  const { data } = await http.get<AnalyticsSummary>('/api/analytics/summary');
  return data;
}

/** One day on the panel's trend charts (UTC calendar day). */
export interface AnalyticsTimeseriesPoint {
  date: string;
  opens: number;
  orders: number;
  revenue: number;
}

/** The in-app performance panel for a store over a UTC date range. */
export interface AnalyticsPanel {
  range: { from: string; to: string };
  /** ISO 4217 code for revenue formatting, or null when unknown. */
  currency: string | null;
  cards: {
    opens: number;
    orders: number;
    /** Orders ÷ opens, 0..1. */
    conversionRate: number;
    revenue: number;
    aov: number;
  };
  quantityOffers: {
    selections: number;
    revenueContribution: number;
    discountGiven: number;
  };
  timeseries: AnalyticsTimeseriesPoint[];
  lifetimeRevenue: number;
}

/** Fetch the performance panel. `from`/`to` are inclusive UTC calendar days (`YYYY-MM-DD`). */
export async function getAnalyticsPanel(from: string, to: string): Promise<AnalyticsPanel> {
  const { data } = await http.get<AnalyticsPanel>('/api/analytics/panel', {
    params: { from, to },
  });
  return data;
}

/** One row of the dashboard's recent-orders list. */
export interface RecentOrder {
  /** Nuvemshop order id. */
  id: string;
  /** Customer-facing order number; null for orders created before we persisted it. */
  number: number | null;
  total: number;
  currency: string | null;
  createdAt: string;
}

/** Dashboard data the config and settings cannot supply: order figures and the OAuth grant. */
export interface DashboardData {
  /** All-time revenue — the same figure the Analytics celebration banner shows. */
  lifetimeRevenue: number;
  currency: string | null;
  recentOrders: RecentOrder[];
  /** Scopes the app needs but the store has not granted (see `getDashboard` on the backend). */
  missingScopes: string[];
  /** Where to send the merchant to re-grant them. */
  authorizeUrl: string;
  /** The store's own order list in the Nuvemshop admin; null when the store domain is unknown. */
  ordersAdminUrl: string | null;
}

export async function getDashboard(): Promise<DashboardData> {
  const { data } = await http.get<DashboardData>('/api/dashboard');
  return data;
}

/** A store product as returned to the offer editor's product picker. */
export interface AdminProduct {
  id: number;
  name: string;
  image?: string;
  /** Effective unit price (sale price when on promotion, else regular), in major units. */
  price?: number;
  /** Selectable variants (e.g. "Black / XL"); empty when the product has no options. `values`
   * are the per-option-type parts (["Black","XL"]) that drive the split variant pickers. */
  variants: { id: number; name: string; values: string[] }[];
  /** Option-type names (e.g. ["Color","Talla"]), aligned to each variant's `values`. */
  variant_options: string[];
}

export async function getProducts(query: string, page = 1): Promise<AdminProduct[]> {
  const { data } = await http.get<{ products: AdminProduct[] }>('/api/products', {
    params: { q: query || undefined, page },
  });
  return data.products;
}

/** Fetch a coverage dataset (province/city/postal regions) so the live preview can show real
 * dropdowns. Hits the same public endpoint the storefront uses (no session required). */
export async function getCoverage(id: string): Promise<CoverageData> {
  const { data } = await http.get<CoverageData>(`/public/coverage/${encodeURIComponent(id)}`);
  return data;
}

/** Upload a custom-field image; returns the public URL to store in the field config. */
// --- Tracking pixels ---

/** One pixel as submitted by the admin. `access_token`: a string sets/replaces the stored
 * token, `null` clears it, `undefined` leaves it untouched (it is never returned). */
export type TrackingPixelInput = Omit<TrackingPixel, 'has_token'> & {
  access_token?: string | null;
};

export interface TrackingInput {
  enabled: boolean;
  require_consent?: boolean;
  pixels: TrackingPixelInput[];
}

/** A pixel as returned by the admin API: stored fields + token presence + last server-side
 * health. The access token itself is never returned. */
export interface AdminTrackingPixel extends TrackingPixel {
  failing: boolean;
  last_error: string | null;
}

export interface AdminTrackingView {
  enabled: boolean;
  require_consent: boolean;
  pixels: AdminTrackingPixel[];
}

export async function getTracking(): Promise<AdminTrackingView> {
  const { data } = await http.get<AdminTrackingView>('/api/tracking');
  return data;
}

export async function putTracking(input: TrackingInput): Promise<AdminTrackingView> {
  const { data } = await http.put<AdminTrackingView>('/api/tracking', input);
  return data;
}

export interface TestPixelResult {
  ok: boolean;
  events_received: number | null;
}

export async function testPixel(pixelId: string, testEventCode?: string): Promise<TestPixelResult> {
  const { data } = await http.post<TestPixelResult>(`/api/tracking/${pixelId}/test`, {
    test_event_code: testEventCode || undefined,
  });
  return data;
}
